<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar
      title="Indicateurs contraventions"
      subtitle="Statistiques et données sur les contraventions des véhicules"
    >
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les statistiques'"
          @click="exportStats"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <router-link to="/vehicules" class="btn btn-secondary">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <!-- Loading indicator -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>

    <div v-else class="row mt-3">
      <!-- Cartes de statistiques générales -->
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-primary clickable"
          :class="{ active: filtreStatut === null }"
          @click="setFiltreStatut(null)"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-primary">Total des contraventions</h5>
            <div class="display-4">{{ contraventions.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-danger clickable"
          :class="{ active: filtreStatut === 0 }"
          @click="setFiltreStatut(0)"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-danger">En attente de paiement</h5>
            <div class="display-4">
              {{ getContraventionsByStatut(0).length }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-warning clickable"
          :class="{ active: filtreStatut === 1 }"
          @click="setFiltreStatut(1)"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-warning">À désigner</h5>
            <div class="display-4">
              {{ getContraventionsByStatut(1).length }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-success clickable"
          :class="{ active: filtreStatut === 3 }"
          @click="setFiltreStatut(3)"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-success">Payées</h5>
            <div class="display-4">
              {{ getContraventionsByStatut(3).length }}
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div
            class="card-header bg-secondary text-white d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 class="mb-0">{{ getTitreContraventions }}</h5>
            </div>
            <div class="d-flex align-items-center">
              <div class="mr-3 d-flex align-items-center">
                <span class="text-white-50 mr-2">Afficher</span>
                <select
                  v-model="nombreContraventionsAffichees"
                  class="form-control form-control-sm"
                  style="width: auto"
                >
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
              <button
                v-if="filtreStatut !== null"
                class="btn btn-sm btn-outline-light"
                @click="setFiltreStatut(null)"
              >
                Réinitialiser le filtre
              </button>
            </div>
          </div>
          <div class="card-body">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Véhicule</th>
                  <th>Type</th>
                  <th>Cause</th>
                  <th>Montant</th>
                  <th>Collaborateur</th>
                  <th>Statut</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="contrav in dernieresContraventions"
                  :key="contrav.id"
                >
                  <td>{{ formatDate(contrav.date) }}</td>
                  <td>
                    <span class="badge badge-light">{{
                      contrav.immatriculation
                    }}</span>
                    {{ contrav.modele }}
                  </td>
                  <td>{{ getTypeLabel(contrav.type) }}</td>
                  <td>{{ getCauseLabel(contrav.cause_infraction) }}</td>
                  <td>{{ formatCurrency(contrav.montant_amende) }}</td>
                  <td>
                    <span v-if="contrav.collaborateur_nom">
                      {{ contrav.collaborateur_nom }}
                      {{ contrav.collaborateur_prenom }}
                    </span>
                    <span v-else class="text-muted">Non désigné</span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="getStatutBadgeClass(contrav.statut)"
                    >
                      {{ getStatutLabel(contrav.statut) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        `/vehicules/detail/${contrav.vehicule_id}?tab=contraventions`
                      "
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="fas fa-eye mr-1"></i> Voir
                    </router-link>
                  </td>
                </tr>
                <tr v-if="dernieresContraventions.length === 0">
                  <td colspan="8" class="text-center">
                    {{
                      filtreStatut !== null
                        ? `Aucune contravention avec le statut "${getStatutLabel(
                            filtreStatut
                          )}" trouvée`
                        : "Aucune contravention enregistrée"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Graphique évolution mensuelle -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Évolution mensuelle</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <!-- Remplacer la div par apexchart -->
              <apexchart
                type="line"
                height="300"
                :options="monthlyChartOptions"
                :series="monthlySeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Répartition par type de contravention -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">Répartition par type de contravention</h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Type</th>
                  <th class="text-center">Nombre</th>
                  <th class="text-center">Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(count, index) in contraventionsParType"
                  :key="index"
                >
                  <td>{{ getTypeLabel(index) }}</td>
                  <td class="text-center">{{ count }}</td>
                  <td class="text-center">
                    {{ ((count / contraventions.length) * 100).toFixed(1) }}%
                    <div class="progress mt-1" style="height: 5px;">
                      <div
                        class="progress-bar bg-primary"
                        :style="
                          `width: ${(count / contraventions.length) * 100}%`
                        "
                      ></div>
                    </div>
                  </td>
                </tr>
                <tr v-if="Object.keys(contraventionsParType).length === 0">
                  <td colspan="3" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Répartition par cause d'infraction -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-info text-white">
            <h5 class="mb-0">Répartition par cause d'infraction</h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Cause</th>
                  <th class="text-center">Nombre</th>
                  <th class="text-center">Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(count, index) in contraventionsParCause"
                  :key="index"
                >
                  <td>{{ getCauseLabel(index) }}</td>
                  <td class="text-center">{{ count }}</td>
                  <td class="text-center">
                    {{ ((count / contraventions.length) * 100).toFixed(1) }}%
                    <div class="progress mt-1" style="height: 5px;">
                      <div
                        class="progress-bar bg-info"
                        :style="
                          `width: ${(count / contraventions.length) * 100}%`
                        "
                      ></div>
                    </div>
                  </td>
                </tr>
                <tr v-if="Object.keys(contraventionsParCause).length === 0">
                  <td colspan="3" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Top 10 des véhicules avec le plus de contraventions -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-warning text-dark">
            <h5 class="mb-0">
              Top 10 des véhicules avec le plus de contraventions
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th class="text-center">Nb contraventions</th>
                  <th class="text-center">Montant total</th>
                  <th class="text-center">Points perdus</th>
                  <th class="text-center">Détails</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicule in topVehiculesContraventions"
                  :key="vehicule.immatriculation"
                >
                  <td>{{ vehicule.modele || "Non défini" }}</td>
                  <td>{{ vehicule.immatriculation || "Non défini" }}</td>
                  <td class="text-center">
                    <span class="badge badge-warning">
                      {{ vehicule.nbContraventions }}
                    </span>
                  </td>
                  <td class="text-center">
                    {{ formatCurrency(vehicule.montantTotal) }}
                  </td>
                  <td class="text-center">
                    <span class="badge badge-danger">
                      {{ vehicule.pointsPerdus }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        '/vehicules?immatriculation=' + vehicule.immatriculation
                      "
                      class="btn btn-sm btn-outline-info"
                    >
                      Détails
                    </router-link>
                  </td>
                </tr>
                <tr v-if="topVehiculesContraventions.length === 0">
                  <td colspan="6" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import moment from "moment";

export default {
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      nombreContraventionsAffichees: 20,
      filtreStatut: null,
      contraventions: [],
      loading: false,
      chart: null,
      typesContravention: [
        { id: 0, description: "Avis de contravention" },
        { id: 1, description: "FPS" },
        { id: 2, description: "Avis de paiement des péages" }
      ],
      causesInfraction: [
        { id: 0, description: "Excès de vitesse" },
        { id: 1, description: "Conduite dangereuse" },
        { id: 2, description: "Appel au volant" },
        { id: 3, description: "Autre" }
      ],
      statutsContravention: [
        { id: 0, description: "En attente de paiement" },
        { id: 1, description: "À désigner" },
        { id: 2, description: "Désigné" },
        { id: 3, description: "Payée" }
      ]
    };
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    setFiltreStatut(statutId) {
      // Basculer le filtre si on clique à nouveau sur le même statut
      this.filtreStatut = this.filtreStatut === statutId ? null : statutId;
    },
    fetchData() {
      this.loading = true;

      this.$http
        .get("/vehicules-contraventions")
        .then(response => {
          // Vérifier si les données sont déjà au format JSON ou si c'est un CSV à parser
          if (typeof response.data === "string") {
            // Si c'est un format CSV, il faudrait le parser
            console.warn(
              "Les données reçues sont au format texte, pas au format JSON"
            );
            this.contraventions = [];
          } else {
            // Si c'est déjà du JSON
            this.contraventions = response.data;
          }

          this.$nextTick(() => {
            this.initChart();
          });
        })
        .catch(error => {
          console.error(
            "Erreur lors du chargement des données:",
            errHandler(error)
          );
          this.$toast.error(
            "Impossible de charger les données des contraventions"
          );
          this.contraventions = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getMonthlyData() {
      const monthlyData = {};
      const monthlyAmounts = {};
      const now = moment();
      const sixMonthsAgo = moment().subtract(6, "months");

      // Initialiser les 6 derniers mois avec des compteurs à 0
      for (
        let m = moment(sixMonthsAgo);
        m.isSameOrBefore(now, "month");
        m.add(1, "month")
      ) {
        const monthKey = m.format("YYYY-MM");
        monthlyData[monthKey] = 0;
        monthlyAmounts[monthKey] = 0;
      }

      // Remplir les données réelles
      this.contraventions.forEach(contrav => {
        if (!contrav.date) return;

        const monthKey = moment(contrav.date).format("YYYY-MM");
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey]++;
          monthlyAmounts[monthKey] += parseFloat(contrav.montant_amende) || 0;
        }
      });

      // Formater pour ApexCharts
      const labels = Object.keys(monthlyData).map(monthKey =>
        moment(monthKey, "YYYY-MM").format("MMM YYYY")
      );

      return {
        labels,
        counts: Object.values(monthlyData),
        amounts: Object.values(monthlyAmounts)
      };
    },

    exportStats() {
      this.loading = true;

      this.$http
        .get("/vehicules-contraventions-export", {
          responseType: "blob" // Important pour télécharger le fichier
        })
        .then(response => {
          // Créer un lien de téléchargement pour le fichier
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          // Récupérer le nom du fichier depuis l'en-tête de la réponse si disponible
          const contentDisposition = response.headers["content-disposition"];
          let filename = "contraventions-export.xls";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch.length === 2) {
              filename = filenameMatch[1];
            }
          }

          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$toast.success("Export téléchargé avec succès");
        })
        .catch(error => {
          console.error("Erreur lors de l'export:", errHandler(error));
          this.$toast.error("Impossible d'exporter les données");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getTypeLabel(typeId) {
      const type = this.typesContravention.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },

    getCauseLabel(causeId) {
      const cause = this.causesInfraction.find(c => c.id === parseInt(causeId));
      return cause ? cause.description : "Non définie";
    },

    getStatutLabel(statutId) {
      const statut = this.statutsContravention.find(
        s => s.id === parseInt(statutId)
      );
      return statut ? statut.description : "Non défini";
    },

    getContraventionsByStatut(statutId) {
      return this.contraventions.filter(c => parseInt(c.statut) === statutId);
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return "0,00 €";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR"
      }).format(value);
    },

    getStatutBadgeClass(statutId) {
      statutId = parseInt(statutId);
      switch (statutId) {
        case 0:
          return "badge-warning"; // En attente de paiement
        case 1:
          return "badge-danger"; // À désigner
        case 2:
          return "badge-info"; // Désigné
        case 3:
          return "badge-success"; // Payée
        default:
          return "badge-secondary";
      }
    },

    // Assurez-vous que cette méthode existe
    formatDate(date) {
      if (!date) return "Non défini";
      return moment(date).format("DD/MM/YYYY");
    }
  },
  computed: {
    monthlySeries() {
      const monthlyData = this.getMonthlyData();
      return [
        {
          name: "Nombre de contraventions",
          type: "column",
          data: monthlyData.counts
        },
        {
          name: "Montant total (€)",
          type: "line",
          data: monthlyData.amounts
        }
      ];
    },

    monthlyChartOptions() {
      const monthlyData = this.getMonthlyData();
      return {
        chart: {
          type: "line",
          height: 300,
          toolbar: {
            show: true
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          },
          background: "transparent"
        },
        stroke: {
          curve: "smooth",
          width: [3, 3]
        },
        colors: ["#36A2EB", "#FF6384"],
        fill: {
          type: ["solid", "solid"],
          opacity: [0.7, 0.3]
        },
        labels: monthlyData.labels,
        xaxis: {
          type: "category",
          labels: {
            rotateAlways: false
          }
        },
        yaxis: [
          {
            title: {
              text: "Nombre de contraventions"
            },
            labels: {
              formatter: function(val) {
                return val.toFixed(0);
              }
            }
          },
          {
            opposite: true,
            title: {
              text: "Montant (€)"
            },
            labels: {
              formatter: function(val) {
                return new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(val);
              }
            }
          }
        ],
        dataLabels: {
          enabled: false
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function(value, { seriesIndex }) {
              if (seriesIndex === 0) {
                return value.toFixed(0) + " contraventions";
              } else {
                return new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR"
                }).format(value);
              }
            }
          }
        },
        legend: {
          position: "top"
        },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 5
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },
    contraventionsParType() {
      const typeCount = {};

      this.typesContravention.forEach(type => {
        typeCount[type.id] = 0;
      });

      this.contraventions.forEach(contravention => {
        const typeId = parseInt(contravention.type);
        if (typeCount[typeId] !== undefined) {
          typeCount[typeId]++;
        } else {
          typeCount[typeId] = 1;
        }
      });

      return typeCount;
    },

    contraventionsParCause() {
      const causeCount = {};

      this.causesInfraction.forEach(cause => {
        causeCount[cause.id] = 0;
      });

      this.contraventions.forEach(contravention => {
        const causeId = parseInt(contravention.cause_infraction);
        if (causeCount[causeId] !== undefined) {
          causeCount[causeId]++;
        } else {
          causeCount[causeId] = 1;
        }
      });

      return causeCount;
    },

    topVehiculesContraventions() {
      // Regrouper par immatriculation
      const vehiculesMap = {};

      this.contraventions.forEach(contravention => {
        const immat = contravention.immatriculation;
        if (!immat) return;

        if (!vehiculesMap[immat]) {
          vehiculesMap[immat] = {
            immatriculation: immat,
            modele: contravention.modele,
            vehicule_id: contravention.vehicule_id,
            nbContraventions: 0,
            montantTotal: 0,
            pointsPerdus: 0
          };
        }

        vehiculesMap[immat].nbContraventions++;
        vehiculesMap[immat].montantTotal +=
          parseFloat(contravention.montant_amende) || 0;
        vehiculesMap[immat].pointsPerdus +=
          parseInt(contravention.nombre_points_perdus) || 0;
      });

      // Convertir en tableau et trier
      return Object.values(vehiculesMap)
        .sort((a, b) => b.nbContraventions - a.nbContraventions)
        .slice(0, 20);
    },

    dernieresContraventions() {
      // Filtrer d'abord par statut si un filtre est actif
      let contraventionsFiltered = this.contraventions;
      if (this.filtreStatut !== null) {
        contraventionsFiltered = this.getContraventionsByStatut(
          this.filtreStatut
        );
      }

      // Filtrer les contraventions qui ont une date
      const contraventionsAvecDate = contraventionsFiltered.filter(c => c.date);

      // Trier par date décroissante (plus récent d'abord)
      return [...contraventionsAvecDate]
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA;
        })
        .slice(0, this.nombreContraventionsAffichees); // Prendre les résultats selon le filtre
    }, // Nouvelle propriété calculée pour le titre dynamique
    getTitreContraventions() {
      let titre =
        this.filtreStatut === null
          ? "Dernières contraventions"
          : `Contraventions avec statut : ${this.getStatutLabel(
              this.filtreStatut
            )}`;

      // Ajouter le nombre d'éléments affichés au titre
      const nbTotal =
        this.filtreStatut === null
          ? this.contraventions.length
          : this.getContraventionsByStatut(this.filtreStatut).length;

      return `${titre} (${Math.min(
        this.nombreContraventionsAffichees,
        nbTotal
      )} sur ${nbTotal})`;
    }
  }
};
</script>

<style lang="css">
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  font-weight: 500;
}

.table th {
  background-color: #f8f9fa;
}

.progress {
  border-radius: 0.5rem;
  overflow: hidden;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.75em;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.border-primary {
  border-top: 4px solid #007bff !important;
}

.border-info {
  border-top: 4px solid #17a2b8 !important;
}

.border-success {
  border-top: 4px solid #28a745 !important;
}

.border-warning {
  border-top: 4px solid #ffc107 !important;
}

.border-danger {
  border-top: 4px solid #dc3545 !important;
}

.chart-container {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable:hover {
  transform: translateY(-4px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.2);
}

.card.active {
  transform: translateY(-4px);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.3);
}

.card.active::before {
  content: "✓";
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #28a745;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.border-primary.active {
  border-color: #007bff !important;
  border-width: 4px !important;
}

.border-danger.active {
  border-color: #dc3545 !important;
  border-width: 4px !important;
}

.border-warning.active {
  border-color: #ffc107 !important;
  border-width: 4px !important;
}

.border-success.active {
  border-color: #28a745 !important;
  border-width: 4px !important;
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1);
}

.bg-warning-light {
  background-color: rgba(255, 193, 7, 0.1);
}
</style>
